import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import StreamIcon from "../../../assets/images/icons/stream.svg";
import EditIcon from "../../../assets/images/admin/edit-icon.svg";
import CategoryIcon from "../../../assets/images/admin/category-icon.svg";
import NotesIcon from "../../../assets/images/admin/notes-icon.svg";
import EntryModal from './EntryModal';
import MonthDropdown from "./MonthDropdown";
import * as actions from "../../../redux/actions/artistManager";
import StarIcon from "../../../assets/images/icons/start-icon.svg";


const revenue_categories = [
    "Select",
    "Custom",
    "Revenue",
    "Streaming",
    "Marketing",
    "Content",
    "Merch",
    "Shows",
    "PR, Press & Sync",
    "Publishing",
    "Brand Deals",
    "Learning",
    "Operations",
    "Memberships",
    "Distributions",
];

export const LogRevenue = ({
    dspEstimatesEnabled = true,
    needsLinkArtist = false,
    onLinkArtist,
    onSubscribe,
}) => {
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [action, setAction] = useState(false);
    const [month, setMonth] = useState('');
    const [year, setYear] = useState('');

    const artistManagerReducer = useSelector((state) => state.artistManager);
    const { hasActiveSubscription } = useSelector((state) => state.billing);
    const activeArtistProfileId = useSelector(
        (state) => state.auth.access?.artist?.activeArtistProfileId || null
    );
    
    // Memoize derived values
    const revenues = useMemo(() => 
        artistManagerReducer?.loggedRevenues?.data?.revenues || [], 
        [artistManagerReducer?.loggedRevenues?.data?.revenues]
    );
    
    const revenueById = useMemo(() => 
        artistManagerReducer?.revenueById || {}, 
        [artistManagerReducer?.revenueById]
    );
    
    const totalRevenues = useMemo(() => 
        artistManagerReducer?.loggedRevenues?.data?.totalAmount || 0,
        [artistManagerReducer?.loggedRevenues?.data?.totalAmount]
    );
    
    const spotifyStreamsData = useMemo(() => 
        artistManagerReducer?.spotifyDataForRevenue || {},
        [artistManagerReducer?.spotifyDataForRevenue]
    );
    
    const youtubeViewsData = useMemo(() => 
        artistManagerReducer?.youtubeDataForRevenue || {},
        [artistManagerReducer?.youtubeDataForRevenue]
    );

    // Memoize calculations
    const calculations = useMemo(() => {
        const streamsPlusViews = (spotifyStreamsData?.streamsDelta || 0) + (youtubeViewsData?.streamsDelta || 0);
        const totalPublishingEstimate = (streamsPlusViews * 0.0004) + (streamsPlusViews * 0.00091);
        const totalStreamingEstimate = (spotifyStreamsData?.streamsDelta * 0.0038) + (youtubeViewsData?.streamsDelta * 0.0006);
        const totalRevenuesWithEstimates = totalRevenues + totalStreamingEstimate + totalPublishingEstimate;

        return {
            totalPublishingEstimate,
            totalStreamingEstimate,
            totalRevenuesWithEstimates
        };
    }, [spotifyStreamsData, youtubeViewsData, totalRevenues]);

    // Memoize callbacks
    const handleOpenModal = useCallback((e, id = 0) => {
        setIsModalOpen(true);
        if (e === "edit" && id !== 0) {
            setAction(e);
            dispatch(actions.fetchLogRevenueById(toast, id, () => {
                setIsLoading(false);
            }));
        } else {
            setAction(e);
        }
    }, [dispatch]);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const handleModalSubmit = useCallback((payload) => {
        if (hasActiveSubscription) {
            if (payload && payload.action === 'delete') {
                dispatch(actions.deleteLogRevenue(toast, payload.id, {}, month, year, setIsLoading, handleCloseModal));
                return;
            }

            const reqBody = {
                name: payload.name,
                amount: payload.amount,
                source: payload.source,
                category: payload.category,
                notes: payload.notes,
            };

            if (action === "edit") {
                dispatch(actions.updateLogRevenue(toast, payload.id, reqBody, month, year, setIsLoading, handleCloseModal));
            } else {
                dispatch(actions.createLogRevenue(toast, reqBody, month, year, setIsLoading, handleCloseModal));
            }
        }
    }, [action, month, year, dispatch, handleCloseModal]);

    // Memoize month setter to ensure it's called with initial value
    const handleMonthChange = useCallback((newMonth) => {
        setMonth(newMonth);
    }, []);

    const handleYearChange = useCallback((newYear) => {
        setYear(newYear);
    }, []);

    useEffect(() => {
        if (!month || !year) return;

        setIsLoading(true);

        const fetches = [
            dispatch(actions.fetchLogRevenues(toast, month, year, setIsLoading, "log_revenue")),
        ];

        if (dspEstimatesEnabled) {
            fetches.push(
                dispatch(actions.getStreamsData(toast, { source: "spotify", month, year }, setIsLoading, "log_revenue")),
                dispatch(actions.getStreamsData(toast, { source: "youtube", month, year }, setIsLoading, "log_revenue"))
            );
        }

        Promise.all(fetches).finally(() => {
            setIsLoading(false);
        });
    }, [month, year, dspEstimatesEnabled, dispatch, activeArtistProfileId]);

    // Memoize quick action cards
    const QuickActionCard = useCallback(({ title, icon, iconStyle = {} }) => (
        <div 
            onClick={() => handleOpenModal("add")} 
            className="bg-theme_clr2 w-[80px] h-[80px] rounded-xl text-center flex-1 cursor-pointer p-2"
        >
            <div className="name text-sm text-white mb-2">{title}</div>
            <div className="icon">
                {typeof icon === 'string' && icon.startsWith('bi-') ? (
                    <i className={`bi ${icon} text-3xl leading-none text-white`}></i>
                ) : (
                    <img src={icon} alt={`${title} Icon`} className="w-[30px] h-[30px] mx-auto" style={iconStyle} />
                )}
            </div>
        </div>
    ), [handleOpenModal]);

    const displayedMonthlyTotal = dspEstimatesEnabled
        ? calculations.totalRevenuesWithEstimates
        : totalRevenues;

    const handleUnlockClick = needsLinkArtist ? onLinkArtist : onSubscribe;
    const unlockCtaLabel = needsLinkArtist ? "Link Your Artist" : "Subscribe to unlock";

    const DspEstimateInlineRow = ({ label, value, description }) => {
        if (dspEstimatesEnabled) {
            return (
                <div className="bg-white font-semibold flex flex-wrap justify-center gap-1 rounded-xl p-2">
                    <span>
                        {label}: ${value.toFixed(2)}
                    </span>
                </div>
            );
        }

        return (
            <div className="bg-white font-inter flex flex-col gap-2 rounded-xl px-3 py-2 text-theme_clr1 sm:flex-row sm:items-center sm:justify-between">
                <span className="font-semibold text-sm shrink-0">{label}:</span>
                <div className="flex flex-col items-end gap-1.5 text-right">
                    <p className="text-xs leading-snug text-slate-600">{description}</p>
                    <button
                        type="button"
                        className="btn-custom3 inline-flex w-fit items-center justify-center gap-1.5 !px-3 !py-1.5 text-xs"
                        onClick={handleUnlockClick}
                    >
                        {unlockCtaLabel}
                        {!needsLinkArtist && (
                            <img
                                src={StarIcon}
                                alt=""
                                className="h-[14px] w-[14px] min-w-[14px]"
                            />
                        )}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="info-box h-full bg-theme_clr1 font-inter font-medium flex flex-col rounded-xl text-[15px] text-theme_clr1 p-4 xl:p-5">
            <div className="header-section flex flex-wrap items-center justify-between gap-2 text-white mb-4">
                <div className="title-info text-[23px] font-bold font-manrope flex items-center gap-2 leading-tight">
                    <img 
                        src={require('../../../assets/images/admin/Amplitude-Info.png')} 
                        alt="Amplitude Info" 
                        className="w-[30px] h-[30px]" 
                    /> 
                    Log Revenue
                </div>
                <button 
                    type="button" 
                    onClick={() => handleOpenModal("add")} 
                    className="btn-custom3"
                >
                    Add Entry <i className="bi bi-plus-circle text-base ms-1"></i>
                </button>
            </div>

            <div className="content-wrapper flex flex-col gap-2">
                <div className="bg-white rounded-xl p-4">
                    <div className="text-theme_clr1 text-sm mb-2">
                        Enter the details below to log revenue from music, merch, and more.
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <QuickActionCard title="Name" icon={EditIcon} />
                        <QuickActionCard title="Amount" icon="bi-currency-dollar" />
                        <QuickActionCard 
                            title="Source" 
                            icon={StreamIcon} 
                            iconStyle={{ filter: "brightness(0) invert(1)" }} 
                        />
                        <QuickActionCard title="Category" icon={CategoryIcon} />
                        <QuickActionCard title="Notes" icon={NotesIcon} />
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4">
                    <div className="wrapper mb-2">
                        <MonthDropdown
                            getMonth={handleMonthChange}
                            getYear={handleYearChange}
                        />
                    </div>
                    <div className="table-responsive max-h-[117px] overflow-auto">
                        {isLoading ? (
                            <div className="text-center text-theme_clr1 py-4">Loading...</div>
                        ) : revenues.length > 0 ? (
                            <table className="min-w-[450px] w-full text-left text-sm text-theme_clr1">
                                <thead>
                                    <tr>
                                        <th className="p-1">Name</th>
                                        <th className="p-1">Amount</th>
                                        <th className="p-1">Source</th>
                                        <th className="p-1">Category</th>
                                        <th className="p-1">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {revenues.map((revenue) => (
                                        <tr 
                                            key={revenue._id} 
                                            className="border-t hover:bg-orange-50 transition-colors"
                                        >
                                            <td className="p-1 font-medium">{revenue.name}</td>
                                            <td className="p-1">${revenue.amount}</td>
                                            <td className="p-1">{revenue.source}</td>
                                            <td className="p-1">{revenue.category}</td>
                                            <td className="p-1">
                                                <button 
                                                    onClick={() => handleOpenModal("edit", revenue._id)} 
                                                    className="text-orange-600 font-semibold"
                                                >
                                                    View/Edit
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="text-center text-theme_clr1 py-4">
                                No Revenue Logged for this month.
                            </div>
                        )}
                    </div>
                </div>

                <DspEstimateInlineRow
                    label="Streaming Estimate (from your DSP data)"
                    value={calculations.totalStreamingEstimate}
                    description="See projected streaming revenue based on your real-time DSP performance."
                />
                <DspEstimateInlineRow
                    label="Publishing Estimate (from your Royalties data)"
                    value={calculations.totalPublishingEstimate}
                    description="Estimate potential publishing royalties from your streaming and video performance."
                />
                <div className="bg-white font-semibold flex flex-wrap justify-center gap-1 rounded-xl p-2">
                    <span>
                        Total Logged This Month: ${displayedMonthlyTotal.toFixed(2)}
                    </span>
                </div>
            </div>

            <EntryModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleModalSubmit}
                defaultCategory={"Revenue"}
                categories={revenue_categories}
                action={action}
                initialData={action === "edit" ? revenueById : {}}
            />
            <ToastContainer />
        </div>
    );
};